import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Users,
    Bot,
    DollarSign,
    TrendingUp,
    Settings,
    ArrowUpRight,
    ArrowDownRight,
    UserCheck,
    Percent,
    Save,
    Search,
    MoreVertical,
    Pencil,
    Trash2,
    Eye,
    ShoppingCart,
    Tag,
    Clock,
    Layers,
    Link2,
    CreditCard
} from 'lucide-react';
import api from '../services/api';
import { GlassButton } from '../components/ui/GlassButton';
import { DateFilter } from '../components/dashboard/DateFilter';
import { StatCard } from '../components/dashboard/StatCard';
import { DashboardChart } from '../components/dashboard/DashboardChart';
import { TopSellersList } from '../components/dashboard/TopSellersList';

interface AdminStats {
    total_revenue: number;
    total_fees: number;
    total_sales: number;
    average_ticket: number;
    total_pending: number;
    active_users: number;
    active_bots: number;
    total_groups: number;
    total_plans: number;
    total_checkouts: number;
    total_withdrawals: number;
    chart_data: any[];
    top_sellers: any[];
}

export const Admin: React.FC = () => {
    const [stats, setStats] = useState<AdminStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [initialLoad, setInitialLoad] = useState(true);
    const navigate = useNavigate();

    // Date Filters
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);

    // Initial Date Logic (Month)
    useEffect(() => {
        const now = new Date();
        const start = new Date(now.getFullYear(), now.getMonth(), 1);
        setStartDate(start.toISOString());
    }, []);

    useEffect(() => {
        loadAdminData();
    }, [startDate, endDate]);

    const loadAdminData = async () => {
        try {
            setLoading(true);
            const params: any = {};
            if (startDate) params.startDate = startDate;
            if (endDate) params.endDate = endDate;

            const [statsRes] = await Promise.all([
                api.get('/banana/stats', { params }),
            ]);

            setStats(statsRes.data.stats);
            setInitialLoad(false);
        } catch (error) {
            console.error('Erro ao carregar dados admin:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (newStart: string | null, newEnd: string | null) => {
        setStartDate(newStart);
        setEndDate(newEnd);
    };

    if (initialLoad) return <div className="p-8 text-white opacity-50">Carregando painel master...</div>;

    return (
        <div className="admin-view p-6">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                    <Settings className="text-orange-500" /> Painel de Controle Hotlize
                </h1>
                <GlassButton onClick={loadAdminData} loading={loading}>Atualizar Dados</GlassButton>
            </div>

            {/* Date Filters Component */}
            <DateFilter onFilterChange={handleFilterChange} />

            {/* Smaller Stats Grid (KPIs de Venda) */}
            <div className="dashboard-stats-grid mt-6" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                <StatCard
                    title="Faturamento"
                    value={`R$ ${(stats?.total_revenue || 0).toFixed(2)}`}
                    sublabel="No período"
                    MainIcon={DollarSign}
                    SubIcon={TrendingUp}
                    colorBase="#22c55e"
                    colorRgba="rgba(34, 197, 94, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Lucro Hotlize"
                    value={`R$ ${(stats?.total_fees || 0).toFixed(2)}`}
                    sublabel="Taxas retidas"
                    MainIcon={TrendingUp}
                    SubIcon={Percent}
                    colorBase="#ff6a00"
                    colorRgba="rgba(255, 106, 0, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Vendas Pagas"
                    value={stats?.total_sales || 0}
                    sublabel="Quantidade"
                    MainIcon={ShoppingCart}
                    SubIcon={TrendingUp}
                    colorBase="#3b82f6"
                    colorRgba="rgba(59, 130, 246, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Ticket Médio"
                    value={`R$ ${(stats?.average_ticket || 0).toFixed(2)}`}
                    sublabel="Média global"
                    MainIcon={Tag}
                    SubIcon={TrendingUp}
                    colorBase="#a855f7"
                    colorRgba="rgba(168, 85, 247, 0.1)"
                    loading={loading}
                />
            </div>

            {/* Main Content Grid: Chart em destaque */}
            <DashboardChart
                data={stats?.chart_data || []}
                loading={loading}
            />

            {/* Operational Stats Grid (Segunda fileira de KPIs) */}
            <div className="dashboard-stats-grid mt-6" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                marginBottom: '24px'
            }}>
                <StatCard
                    title="Valor Pendente"
                    value={`R$ ${(stats?.total_pending || 0).toFixed(2)}`}
                    sublabel="Aguardando Pagamento"
                    MainIcon={Clock}
                    SubIcon={TrendingUp}
                    colorBase="#fbbf24"
                    colorRgba="rgba(251, 191, 36, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Bots Ativos"
                    value={stats?.active_bots || 0}
                    sublabel="Em execução agora"
                    MainIcon={Bot}
                    SubIcon={TrendingUp}
                    colorBase="#ec4899"
                    colorRgba="rgba(236, 72, 153, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Grupos Vinculados"
                    value={stats?.total_groups || 0}
                    sublabel="Comunidades ativas"
                    MainIcon={Users}
                    SubIcon={TrendingUp}
                    colorBase="#06b6d4"
                    colorRgba="rgba(6, 182, 212, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Planos Criados"
                    value={stats?.total_plans || 0}
                    sublabel="Ofertas no sistema"
                    MainIcon={Layers}
                    SubIcon={TrendingUp}
                    colorBase="#8b5cf6"
                    colorRgba="rgba(139, 92, 246, 0.1)"
                    loading={loading}
                />
                <StatCard
                    title="Checkouts"
                    value={stats?.total_checkouts || 0}
                    sublabel="Links de Pagamento"
                    MainIcon={Link2}
                    SubIcon={TrendingUp}
                    colorBase="#10b981"
                    colorRgba="rgba(16, 185, 129, 0.1)"
                    loading={loading}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 mt-6">
                <div className="lg:col-span-1">
                    <TopSellersList data={stats?.top_sellers || []} loading={loading} />
                </div>
            </div>
        </div>
    );
};
